import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";


export interface KeyVault {
	id: string;
	label: string;
	scope: string;
	updatedAt: string;
}

export interface KeyVaultResponse {
	data: KeyVault[];
	total: number;
}

export interface KeyVaultFormData {
	label: string;
	value: string;
	scope: string;
}

export const keyVaultKeys = {
	all: ["key-vault"] as const,
	lists: () => [...keyVaultKeys.all, "list"] as const,
	list: (query?: string) => [...keyVaultKeys.lists(), { query }] as const,
	details: () => [...keyVaultKeys.all, "detail"] as const,
	detail: (id: string) => [...keyVaultKeys.details(), id] as const,
};

export function useKeyVaults() {
	return useQuery({
		queryKey: keyVaultKeys.lists(),
		queryFn: async () => {
			const response = await apiClient.get("/key-vault");
			return response.data;
		},
	});
}

export function useKeyVault(id: string) {
	return useQuery({
		queryKey: keyVaultKeys.detail(id),
		queryFn: async (): Promise<KeyVault> => {
			const response = await apiClient.get(`/key-vault/${id}`);
			return response.data;
		},
		enabled: !!id,
	});
}

export function useCreateKeyVault() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: KeyVaultFormData): Promise<any> => {
			const response = await apiClient.post("/key-vault", data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: keyVaultKeys.lists() });
			toast.success("Key Vault created successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Something went wrong!");
		},
	});
}

export function useUpdateKeyVault() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: KeyVaultFormData }): Promise<KeyVault> => {
			const response = await apiClient.post(`/key-vault/${id}`, data);
			return response.data;
		},
		onSuccess: (data, variables) => {
			queryClient.setQueryData(keyVaultKeys.detail(variables.id), data);
			queryClient.invalidateQueries({ queryKey: keyVaultKeys.lists() });
			toast.success("Key Vault updated successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Something went wrong!");
		},
	});
}

export function useDeleteKeyVault() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string): Promise<void> => {
			await apiClient.delete(`/key-vault/${id}`);
		},
		onSuccess: (_, id) => {
			queryClient.removeQueries({ queryKey: keyVaultKeys.detail(id) });
			queryClient.invalidateQueries({ queryKey: keyVaultKeys.lists() });
			toast.success("Key Vault deleted successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			toast.error("Something went wrong!");
		},
	});
}
